import { get, post, put, del } from '../api/client';
import { TokenService } from './TokenService';
import logger from '../../utils/logger';
import { ValidationError } from '../../utils/errorHandler';


export const authService = {
  liffCheck: async (idToken) => {
    try {
      const response = await post('/auth/liff-check', {
        idToken: idToken
      });
      
      if (response && response.hasLineId !== undefined) {
        if (response.hasLineId && response.id) {
          const userData = {
            id: response.id,
            username: response.username,
            email: response.email,
            fullName: response.fullnameEn,
            role: response.userRole,
            isActive: response.isActived,
            profileImage: response.profileImage,
            staffId: response.staffId,
            teacherId: response.teacherId
          };

          return {
            hasLineId: true,
            user: userData,
            accessToken: response.accessToken,
            refreshToken: response.refreshToken,
            message: 'LIFF auto-login successful'
          };
        } else {
          return {
            hasLineId: false,
            message: 'LINE account not linked'
          };
        }
      }
      
      throw new ValidationError('Invalid LIFF check response format');
    } catch (error) {
      logger.error('LIFF check failed', { error: error.message });
      
      if (error.response) {
        const message = error.response.data?.message || 'LIFF check failed';
        throw new ValidationError(message);
      } else if (error.request) {
        throw new ValidationError('Network error. Please check your connection.');
      } else {
        throw new ValidationError(error.message || 'LIFF check failed');
      }
    }
  },

  // Login user
  login: async (credentials) => {
    try {
      const loginData = {
        username: credentials.username.trim(),
        password: credentials.password
      };

      if (credentials.idToken) {
        loginData.idToken = credentials.idToken;
      }

      const response = await post('/auth/login', loginData);
      
      if (response) {
        const userData = {
          id: response.id,
          username: response.username,
          email: response.email,
          fullName: response.fullnameEn,
          role: response.userRole,
          isActive: response.isActived,
          profileImage: response.profileImage,
          staffId: response.staffId,
          teacherId: response.teacherId
        };

        return {
          user: userData,
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
          message: 'Login successful'
        };
      }
      
      throw new ValidationError('Invalid response format');
    } catch (error) {
      logger.error('Login failed', { error: error.message, username: credentials.username });
      
      if (error.response) {
        const message = error.response.data?.message || 'Login failed';
        const status = error.response.status;
        
        // For 412 (Incorrect Password) and 403 (Unauthorized Role), preserve the error structure for modal handling
        if (status === 412 || status === 403) {
          throw error; 
        }
        
        throw new ValidationError(message);
      } else if (error.request) {
        throw new ValidationError('Network error. Please check your connection.');
      } else {
        throw new ValidationError(error.message || 'Login failed');
      }
    }
  },

  // Refresh authentication token
  // TODO: Uncomment when backend API is implemented
  /*
  refreshToken: async () => {
    try {
      const refreshToken = TokenService.getRefreshToken();
      if (!refreshToken) {
        throw new ValidationError('No refresh token available');
      }

      const response = await post('/auth/refresh', {
        refreshToken: refreshToken
      });

      if (response && response.accessToken) {
        TokenService.setTokens(response.accessToken, response.refreshToken || refreshToken);
        
        return {
          accessToken: response.accessToken,
          refreshToken: response.refreshToken || refreshToken,
          user: response.user
        };
      }
      
      throw new ValidationError('Invalid refresh response');
    } catch (error) {
      TokenService.removeTokens();
      throw error;
    }
  },
  */
  
  // Logout user from LIFF auto-login
  logoutLiff: async () => {
    try {
      const response = await post('/auth/logout-liff');
      return response;
    } catch (error) {
      logger.warn('LIFF logout API call failed', { error: error.message });
      return { message: 'LIFF logout successful' };
    }
  },
  
  // Verify current token and get user data
  verifyToken: async () => {
    try {
      const accessToken = TokenService.getAccessToken();
      if (!accessToken || !TokenService.isValidToken(accessToken)) {
        throw new ValidationError('No valid access token available');
      }

      const currentUser = TokenService.getUserFromToken(accessToken);
      if (!currentUser) {
        throw new ValidationError('Unable to decode user data from token');
      }
      
      if (!currentUser.teacherId) {
        throw new ValidationError('Teacher ID not found in token');
      }

      const response = await get(`/teacher/get-teacher-detail/${currentUser.teacherId}`);
      
      if (response) {
        const userData = {
          id: response.id,
          username: response.username,
          email: response.email,
          fullName: response.fullnameEn,
          role: response.userRole,
          isActive: response.isActived,
          profileImage: response.profileImage,
          staffId: response.staffId,
          teacherId: response.teacherId
        };

        return userData;
      }
      
      return response;
    } catch (error) {
      logger.error('Token verification error', {
        message: error.message,
        status: error.response?.status,
        url: error.config?.url
      });
      
      if (error.response?.status === 401) {
        throw new ValidationError('Token expired or invalid');
      } else if (error.response?.status === 404) {
        throw new ValidationError('Teacher not found');
      } else if (error.message.includes('Teacher ID not found') || 
                 error.message.includes('Unable to decode')) {
        throw new ValidationError('Invalid token format');
      }
      
      throw new ValidationError(`Token verification failed: ${error.message}`);
    }
  }
};